/**
 * TransferHistoryPage
 * Shows UNE-Credito transfer history from credit-worker
 */

import { useMemo } from 'react';
import { ArrowUpDown, DollarSign, CheckCircle, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/data-display/StatCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { ROUTES } from '@/constants';
import { useTransferHistory } from '@/features/funding/hooks/useTransferHistory';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FUNDING_INSTRUCTIONS },
  { label: 'Historial de Transferencias', path: ROUTES.TRANSFER_HISTORY },
];

const fmt = (v: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v);

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  APPROVED: 'success',
  COMPLETED: 'success',
  REJECTED: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  COMPLETED: 'Completada',
  REJECTED: 'Rechazada',
};

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={`s-${i.toString()}`} variant="rectangular" height={100} />
        ))}
      </div>
      <Skeleton variant="rectangular" height={300} />
    </div>
  );
}

export function TransferHistoryPage() {
  const { transfers, isLoading, isError, refetch } = useTransferHistory();

  const stats = useMemo(() => {
    const list = transfers ?? [];
    const total = list.length;
    const totalAmount = list.reduce((s, t) => s + t.total_amount_mxn, 0);
    const completed = list.filter((t) => t.status === 'APPROVED' || t.status === 'COMPLETED').length;
    const pending = list.filter((t) => t.status === 'PENDING').length;
    return { total, totalAmount, completed, pending };
  }, [transfers]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Historial de Transferencias" subtitle="Transferencias realizadas" breadcrumbs={BREADCRUMBS} />
        <PageSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title="Historial de Transferencias" subtitle="Transferencias realizadas" breadcrumbs={BREADCRUMBS} />
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
          <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Error al cargar historial</h3>
          <Button className="mt-4" onClick={() => refetch()}>
            <RefreshCw size={16} /> Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Historial de Transferencias"
        subtitle="Transferencias realizadas"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="transfer-history" />
            <Button onClick={() => refetch()}><RefreshCw size={16} /> Actualizar</Button>
          </div>
        }
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Transferencias" value={stats.total} icon={ArrowUpDown} />
          <StatCard title="Monto Total" value={fmt(stats.totalAmount)} icon={DollarSign} />
          <StatCard title="Completadas" value={stats.completed} icon={CheckCircle} />
          <StatCard title="Pendientes" value={stats.pending} icon={Clock} />
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-6 py-4">
            <h3 className="text-lg font-semibold text-foreground">Transferencias</h3>
          </div>
          {(transfers ?? []).length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">Sin transferencias</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-6 py-3 font-medium">ID</th>
                    <th className="px-6 py-3 font-medium">Creditos</th>
                    <th className="px-6 py-3 text-right font-medium">Monto</th>
                    <th className="px-6 py-3 text-right font-medium">Registros</th>
                    <th className="px-6 py-3 font-medium">Periodo</th>
                    <th className="px-6 py-3 font-medium">Estado</th>
                    <th className="px-6 py-3 font-medium">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {(transfers ?? []).map((t) => (
                    <tr key={t.transfer_id} className="border-b border-border/50">
                      <td className="px-6 py-3 font-mono text-xs text-foreground">{t.transfer_id}</td>
                      <td className="px-6 py-3 text-muted-foreground">{t.credits_count}</td>
                      <td className="px-6 py-3 text-right tabular-nums font-medium">{fmt(t.total_amount_mxn)}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{t.records_count}</td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {t.period_start} - {t.period_end}
                      </td>
                      <td className="px-6 py-3">
                        <Badge variant={STATUS_VARIANT[t.status] ?? 'default'}>
                          {STATUS_LABEL[t.status] ?? t.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {t.transfer_date ? new Date(t.transfer_date + 'T12:00:00').toLocaleDateString('es-MX') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
